import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import toast from 'react-hot-toast';
import { axiosInstance } from '../axios';
import type { ApiResponse, PaginatedResponse } from '../types/common.types';
import type {
  Part,
  CreatePartRequest,
  UpdatePartRequest,
  AdjustStockRequest,
  PartFilters,
  PartStats,
  LowStockAlert,
  PartStockHistoryList,
  BulkImportPartsResult,
  BulkImportPreviewResult,
} from '../types/part.types';
import type { AddTicketPartRequest } from '../types/ticket.types';
import { ticketKeys } from './useTickets';

const PARTS_KEY = 'parts';

// Query hooks
export function useParts(filters: PartFilters = {}) {
  // Query param names must match PartsController.GetList exactly: searchTerm (via
  // PaginatedRequest), activeOnly, lowStockOnly - not search/isActive/lowStock. Sending the wrong
  // names doesn't error (ASP.NET Core just leaves the unmatched param at its default), so the
  // request "succeeds" but silently returns the unfiltered list.
  const params = new URLSearchParams();
  if (filters.search) params.append('searchTerm', filters.search);
  if (filters.category) params.append('category', filters.category);
  if (filters.isActive !== undefined) params.append('activeOnly', String(filters.isActive));
  if (filters.lowStock) params.append('lowStockOnly', 'true');
  if (filters.page) params.append('page', String(filters.page));
  if (filters.pageSize) params.append('pageSize', String(filters.pageSize));

  return useQuery({
    queryKey: [PARTS_KEY, 'list', filters],
    queryFn: async () => {
      const { data } = await axiosInstance.get<ApiResponse<PaginatedResponse<Part>>>(
        `/parts?${params.toString()}`
      );
      return data;
    },
  });
}

export function usePart(partId: string | undefined) {
  return useQuery({
    queryKey: [PARTS_KEY, 'detail', partId],
    queryFn: async () => {
      const { data } = await axiosInstance.get<ApiResponse<Part>>(`/parts/${partId}`);
      return data;
    },
    enabled: !!partId,
  });
}

export function usePartBySku(sku: string | undefined) {
  return useQuery({
    queryKey: [PARTS_KEY, 'sku', sku],
    queryFn: async () => {
      const { data } = await axiosInstance.get<ApiResponse<Part>>(`/parts/by-sku/${sku}`);
      return data;
    },
    enabled: !!sku,
  });
}

export function usePartStats() {
  return useQuery({
    queryKey: [PARTS_KEY, 'stats'],
    queryFn: async () => {
      const { data } = await axiosInstance.get<ApiResponse<PartStats>>('/parts/stats');
      return data;
    },
  });
}

export function useLowStockParts() {
  return useQuery({
    queryKey: [PARTS_KEY, 'low-stock'],
    queryFn: async () => {
      // Backend returns a bare array here (not wrapped in ApiResponse<T>) - see PartsController.GetLowStockAlerts.
      const { data } = await axiosInstance.get<LowStockAlert[]>('/parts/low-stock');
      return data;
    },
  });
}

export function usePartStockHistory(partId: string | undefined, enabled = true, limit?: number) {
  return useQuery({
    queryKey: [PARTS_KEY, 'stock-history', partId, limit],
    queryFn: async () => {
      const { data } = await axiosInstance.get<ApiResponse<PartStockHistoryList>>(
        `/parts/${partId}/stock-history`,
        { params: limit ? { limit } : undefined }
      );
      return data;
    },
    enabled: enabled && !!partId,
  });
}

export function usePartCategories() {
  return useQuery({
    queryKey: [PARTS_KEY, 'categories'],
    queryFn: async () => {
      const { data } = await axiosInstance.get<ApiResponse<string[]>>('/parts/categories');
      return data;
    },
  });
}

// Mutation hooks
// The frontend's CreatePartRequest/UpdatePartRequest field names (sellingPrice, shelfLocation,
// supplierName) don't match the real backend DTO (sellPrice, location, supplier) - sellPrice is
// `required` server-side, so a create with the old field names always 400'd; the others just
// silently discarded the value. Mapped here rather than renaming the form's field names throughout
// PartsPage.tsx.
function toCreatePayload(request: CreatePartRequest) {
  const { sellingPrice, shelfLocation, supplierName, ...rest } = request;
  return { ...rest, sellPrice: sellingPrice, location: shelfLocation, supplier: supplierName };
}

function toUpdatePayload(request: UpdatePartRequest) {
  const { sellingPrice, shelfLocation, supplierName, ...rest } = request;
  return { ...rest, sellPrice: sellingPrice, location: shelfLocation, supplier: supplierName };
}

export function useCreatePart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreatePartRequest) => {
      const { data } = await axiosInstance.post<ApiResponse<Part>>('/parts', toCreatePayload(request));
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTS_KEY] });
    },
    onError: (error) => {
      const serverMessage = isAxiosError<ApiResponse<never>>(error)
        ? error.response?.data?.message
        : undefined;
      toast.error(serverMessage || 'שגיאה ביצירת החלק');
    },
  });
}

// Bulk-import parts from a CSV file's text content (phase 1: admin-only, see PartsController's
// bulk-import endpoint). No client-side toast here on success/failure counts - the modal renders
// the full per-row result itself (see PartsPage.tsx), which a single toast couldn't show.
// Dry-run before useBulkImportParts actually commits anything - reports which SKUs in the file
// already exist (and would be updated, not duplicated), so the caller can confirm with the user
// first. See PartService.PreviewBulkImportAsync.
export function usePreviewBulkImportParts() {
  return useMutation({
    mutationFn: async (csvContent: string) => {
      const { data } = await axiosInstance.post<ApiResponse<BulkImportPreviewResult>>(
        '/parts/bulk-import/preview',
        { csvContent }
      );
      return data.data!;
    },
    onError: (error) => {
      const serverMessage = isAxiosError<ApiResponse<never>>(error)
        ? error.response?.data?.message
        : undefined;
      toast.error(serverMessage || 'שגיאה בבדיקת הקובץ');
    },
  });
}

export function useBulkImportParts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (csvContent: string) => {
      const { data } = await axiosInstance.post<ApiResponse<BulkImportPartsResult>>(
        '/parts/bulk-import',
        { csvContent }
      );
      return data.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTS_KEY] });
    },
    onError: (error) => {
      const serverMessage = isAxiosError<ApiResponse<never>>(error)
        ? error.response?.data?.message
        : undefined;
      toast.error(serverMessage || 'שגיאה בייבוא הקובץ');
    },
  });
}

export function useUpdatePart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ partId, request }: { partId: string; request: UpdatePartRequest }) => {
      const { data } = await axiosInstance.put<ApiResponse<Part>>(`/parts/${partId}`, toUpdatePayload(request));
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PARTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PARTS_KEY, 'detail', variables.partId] });
    },
    onError: (error) => {
      const serverMessage = isAxiosError<ApiResponse<never>>(error)
        ? error.response?.data?.message
        : undefined;
      toast.error(serverMessage || 'שגיאה בעדכון החלק');
    },
  });
}

export function useAdjustStock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ partId, request }: { partId: string; request: AdjustStockRequest }) => {
      // Backend wants a signed `adjustment` (+add/-remove) plus a required `reason` - not the
      // frontend's separate quantity+type shape. Not wired to any UI today, but fixed so it
      // doesn't 400 the moment someone builds a stock-adjustment screen against this hook.
      const adjustment = request.type === 'remove' ? -Math.abs(request.quantity) : Math.abs(request.quantity);
      const { data } = await axiosInstance.post<ApiResponse<Part>>(
        `/parts/${partId}/adjust-stock`,
        { adjustment, reason: request.reason }
      );
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PARTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PARTS_KEY, 'detail', variables.partId] });
      queryClient.invalidateQueries({ queryKey: [PARTS_KEY, 'stock-history', variables.partId] });
      toast.success('המלאי עודכן');
    },
    onError: (error) => {
      const serverMessage = isAxiosError<ApiResponse<never>>(error)
        ? error.response?.data?.message
        : undefined;
      toast.error(serverMessage || 'שגיאה בעדכון המלאי');
    },
  });
}

// Permanent delete of an archived part; tickets and sales keep their own copy of name/SKU/cost.
export function useDeletePart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (partId: string) => {
      const { data } = await axiosInstance.delete<ApiResponse<null>>(`/parts/${partId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTS_KEY] });
      toast.success('החלק נמחק לצמיתות');
    },
    onError: (error) => {
      const serverMessage = isAxiosError<ApiResponse<never>>(error)
        ? error.response?.data?.message
        : undefined;
      toast.error(serverMessage || 'שגיאה במחיקת החלק');
    },
  });
}

export function useAddPartToTicket(ticketId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: AddTicketPartRequest) => {
      const { data } = await axiosInstance.post<ApiResponse<void>>(
        `/repairtickets/${ticketId}/parts`,
        request
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
      // Consuming a part on a ticket deducts its stock - without this, the parts list and
      // low-stock view keep showing the pre-deduction quantity.
      queryClient.invalidateQueries({ queryKey: [PARTS_KEY] });
      toast.success('החלק נוסף לקריאה');
    },
    onError: (error) => {
      const serverMessage = isAxiosError<ApiResponse<never>>(error)
        ? error.response?.data?.message
        : undefined;
      toast.error(serverMessage || 'שגיאה בהוספת החלק לקריאה');
    },
  });
}

export function useTogglePartActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ partId, isActive }: { partId: string; isActive: boolean }) => {
      const { data } = await axiosInstance.patch<ApiResponse<Part>>(
        `/parts/${partId}/active`,
        { isActive }
      );
      return data;
    },
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: [PARTS_KEY] });
      toast.success(isActive ? 'החלק הוחזר למלאי' : 'החלק הועבר לארכיון');
    },
    onError: (error) => {
      const serverMessage = isAxiosError<ApiResponse<never>>(error)
        ? error.response?.data?.message
        : undefined;
      toast.error(serverMessage || 'שגיאה בעדכון סטטוס החלק');
    },
  });
}
